import { Head, useForm } from '@inertiajs/react'
import { Lock, LogIn, Mail } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { FieldError } from '@/components/feedback/FieldError'

export type SchoolSessionNewProps = {
  defaults: {
    email?: string
    password?: string
  }
}

export default function SchoolSessionNew({ defaults }: SchoolSessionNewProps) {
  const { data, setData, post, processing, errors, reset, transform } = useForm({
    email: defaults.email ?? '',
    password: defaults.password ?? '',
  })
  const baseError = (errors as { base?: string }).base

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    transform((current) => ({ school_coach: current }))
    post('/school/login', {
      preserveScroll: true,
      onFinish: () => reset('password'),
    })
  }

  return (
    <AppShell>
      <Head title="高校コーチログイン" />
      <Card padding="lg" className="space-y-8">
        <header className="space-y-2 text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-white/40">Talentbox for High Schools</p>
          <h1 className="text-2xl font-semibold text-white">高校コーチログイン</h1>
          <p className="text-sm text-white/60">登録済みのメールアドレスとパスワードでログインしてください。</p>
        </header>

        {baseError ? (
          <div className="rounded-2xl border border-danger-500/50 bg-danger-500/10 p-4 text-sm text-danger-200">
            {baseError}
          </div>
        ) : null}

        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2 text-white">
              <Mail className="h-4 w-4 text-brand-200" />
              メールアドレス
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={data.email}
              onChange={(event) => setData('email', event.target.value)}
              placeholder="coach@example.com"
            />
            <FieldError message={errors.email} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="flex items-center gap-2 text-white">
              <Lock className="h-4 w-4 text-brand-200" />
              パスワード
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={data.password}
              onChange={(event) => setData('password', event.target.value)}
            />
            <FieldError message={errors.password} />
          </div>

          <Button type="submit" className="w-full" disabled={processing}>
            <LogIn className="mr-2 h-4 w-4" />
            ログイン
          </Button>
        </form>

        <p className="text-center text-sm">
          <a href="/school/password_resets/new" className="font-semibold text-brand-200 hover:text-brand-100">
            パスワードを忘れた方
          </a>
        </p>

        <p className="text-center text-xs text-white/50">
          まだアカウントをお持ちでない場合は、Talentbox事務局までご連絡ください。
        </p>
      </Card>
    </AppShell>
  )
}
